/* eslint-disable no-unused-vars */
import { ForwardedRef, forwardRef, VideoHTMLAttributes } from 'react'
import { useMedia } from 'react-use'
import NextImage, { ImageProps as NextImageProps } from 'next/image'

import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

export type ImageProps = Omit<NextImageProps, 'src' | 'alt'> &
  Partial<{
    source: WPMedia
    size: 'thumbnail' | 'medium' | 'large' | 'full'
    fit: 'contain' | 'cover'
    alt: string
  }>

export type VideoProps = VideoHTMLAttributes<HTMLVideoElement> &
  Partial<{
    source: WPMedia
    fit: 'contain' | 'cover'
    fill: boolean
  }>

export type MediaProps = {
  media: WPMedia
}

const Image = forwardRef<HTMLImageElement, ImageProps>(
  ({ source, fit = 'cover', fill = true, className, onLoad, ...props }, ref) => {
    const isMobile = useMedia('(max-width: 768px)', false)

    return (
      <div className={cn('relative h-full w-full overflow-hidden', className)}>
        <NextImage
          ref={ref}
          src={
            (isMobile && source?.poster_mobile?.url
              ? source?.poster_mobile?.url
              : source?.poster?.url) || ''
          }
          alt={
            (isMobile && source?.poster_mobile?.alt
              ? source?.poster_mobile?.alt
              : source?.poster?.alt) ?? ''
          }
          className={cn(
            fit === 'contain' ? 'object-contain' : 'object-cover',
            fill ? 'absolute h-full w-full' : 'h-auto w-full'
          )}
          {...props}
          width={
            isMobile && source?.poster_mobile?.width
              ? source?.poster_mobile?.width
              : source?.poster?.width
          }
          height={
            isMobile && source?.poster_mobile?.height
              ? source?.poster_mobile?.height
              : source?.poster?.height
          }
          onLoad={onLoad ? (_img) => onLoad({} as any) : undefined}
        />
      </div>
    )
  }
)

const Video = forwardRef<HTMLVideoElement, VideoProps>(
  (
    {
      source,
      fit = 'cover',
      fill = true,
      className,
      autoPlay = true,
      muted = true,
      loop = true,
      playsInline = true,
      controls = false,
      onLoad,
      ...props
    },
    ref
  ) => {
    const isMobile = useMedia('(max-width: 768px)', false)

    return (
      <div className={cn('relative h-full w-full overflow-hidden', className)}>
        <video
          ref={ref}
          src={
            isMobile && source?.video_mobile?.url ? source?.video_mobile?.url : source?.video.url
          }
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          controls={controls}
          onLoadedData={onLoad}
          className={cn(
            fit === 'contain' ? 'object-contain' : 'object-cover',
            fill ? 'absolute h-full w-full' : 'h-auto w-full'
          )}
          {...props}
        />
      </div>
    )
  }
)

Video.displayName = 'Video'

export const hasVideo = (media: WPMedia) => {
  return !!(media.video?.url || media.video_mobile?.url)
}

const Media = forwardRef<HTMLDivElement, { media: WPMedia } & Partial<ImageProps & VideoProps>>(
  ({ media, className, onLoad, ...props }, ref) => {
    if (!media) return null

    return hasVideo(media) ? (
      <Video
        data-video
        source={media}
        className={className}
        onLoad={onLoad}
        ref={ref as ForwardedRef<HTMLVideoElement>}
        {...props}
      />
    ) : (
      // eslint-disable-next-line jsx-a11y/alt-text
      <Image
        data-image
        source={media}
        className={className}
        onLoad={onLoad}
        ref={ref as ForwardedRef<HTMLImageElement>}
        {...props}
      />
    )
  }
)

Media.displayName = 'Media'

export { Image, Video }
export default Media
